/* eslint react-hooks/exhaustive-deps: "warn" */

import { ChangeEvent, useRef } from 'react';

import cn from 'classnames';

import { Icon } from 'ui/atoms/Icon/Icon';

import styles from './Input.module.scss';

import type { InputProps } from './Input.types';

const sizeClassMap = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
  xl: styles.sizeXl,
};

export const Input = ({
  value = '',
  onChange,
  placeholder,
  label,
  text,
  htmlType = 'text',
  name,
  disabled = false,
  icon,
  size = 'lg',
  theme = 'light',
  error,
  ariaLabel,
  dataTestId,
  fullWidth = false,
  noBorder = false,
  className,
  rows = 3,
  inputRef,
  onKeyDown,
  onFocus,
  onClear,
  autoComplete,
}: InputProps) => {
  const internalRef = useRef<HTMLInputElement>(null);

  if (htmlType === 'textarea') {
    const onTextareaChange = ({ target }: ChangeEvent<HTMLTextAreaElement>) => onChange(target.value);

    return (
      <textarea
        data-test={dataTestId}
        aria-label={ariaLabel}
        className={cn(styles.textarea, disabled && styles.isDisabled, className)}
        placeholder={text || placeholder}
        onChange={onTextareaChange}
        disabled={disabled}
        value={value}
        name={name}
        rows={rows}
      />
    );
  }
  const resolvedRef = inputRef ?? internalRef;

  const onChangeHandler = ({ target }: ChangeEvent<HTMLInputElement>) => onChange(target.value);

  const handleFieldClick = () => {
    resolvedRef.current?.focus();
  };

  return (
    <div
      className={cn(
        styles.form,
        sizeClassMap[size],
        theme === 'dark' && styles.themeDark,
        disabled && styles.isDisabled,
        fullWidth && styles.fullWidth,
        noBorder && styles.noBorder,
        error && styles.hasError,
        label && styles.hasLabel,
        className,
      )}
    >
      <div className={styles.field} onClick={handleFieldClick}>
        {icon && <Icon type={icon} size="small" className={cn(styles.icon, label && styles.fieldIcon)} />}

        {label ? (
          <label className={styles.fieldContent}>
            <span className={styles.fieldLabel}>{label}</span>

            <input
              ref={resolvedRef}
              type={htmlType}
              data-test={dataTestId}
              aria-label={ariaLabel}
              className={styles.input}
              placeholder={text || placeholder}
              onChange={onChangeHandler}
              onKeyDown={onKeyDown}
              onFocus={onFocus}
              autoComplete={autoComplete}
              disabled={disabled}
              value={value}
              name={name}
            />
          </label>
        ) : (
          <input
            ref={resolvedRef}
            type={htmlType}
            data-test={dataTestId}
            aria-label={ariaLabel}
            className={styles.input}
            placeholder={text || placeholder}
            onChange={onChangeHandler}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            autoComplete={autoComplete}
            disabled={disabled}
            value={value}
            name={name}
          />
        )}

        {onClear && value && (
          <button
            className={styles.clearButton}
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            type="button"
            aria-label="Clear"
          >
            <Icon type="close-1" size="small" className={styles.clearIcon} />
          </button>
        )}
      </div>

      {error && (
        <span className={styles.errorLabel} data-test="input-error-label">
          {Array.isArray(error) ? error.join(', ') : error}
        </span>
      )}
    </div>
  );
};
